import React from 'react';
import { motion } from 'framer-motion';
import './Product.css';

const Product = () => {
  const heroStats = [
    { label: '上线企业', value: '200+ 家' },
    { label: '项目效率提升', value: '40%+' },
    { label: '成本控制精度', value: '提升 50%' },
    { label: '风险预警覆盖', value: '30+ 场景' }
  ];

  const painPoints = [
    {
      title: '项目进度不透明',
      description: '多项目并行管理困难，进度信息滞后，延期风险难以及时预警，影响交付与客户信任'
    },
    {
      title: '成本管控粗放',
      description: '成本数据归集滞后，资金使用效率低，超支风险高，缺乏穿透式分析支撑决策'
    },
    {
      title: '合同履约风险高',
      description: '合同管理分散，关键节点缺乏监控，收付款跟踪困难，履约风险难以及时发现'
    },
    {
      title: '审批流程低效',
      description: '依赖线下审批，流程冗长、合规难保障，跨部门协同效率低，拖慢业务响应'
    }
  ];

  const modules = [
    {
      title: '项目一体化管理',
      description:
        '覆盖从立项、设计、施工到竣工验收的全生命周期，让进度、质量、安全、资料在线联动，关键节点自动预警。',
      items: ['进度计划与跟踪', '质量检查与整改', '安全巡检与隐患管理', '资料归档与共享', '里程碑节点管理']
    },
    {
      title: '智能合同风控',
      description:
        '全流程在线流转，提供合同模板、评审、签署、履约监控到变更的闭环管控，自动追踪收付款节点。',
      items: ['合同模板与协同起草', '多级评审与电子签章', '履约节点监控', '变更与索赔管理', '收付款实时预警']
    },
    {
      title: '成本资金看板',
      description:
        '实时归集项目成本，做到投资、支付、回款全链路可视，提供多维度分析报表，支持资金穿透式管控。',
      items: ['投资估算与预算', '成本实时归集', '资金支付与计划', '现金流预测', '多维分析报表']
    },
    {
      title: '自动化流程审批',
      description:
        '高度可配置的流程引擎，支持各类业务审批与电子签章，移动端随时处理，确保合规又高效。',
      items: ['流程自定义配置', '电子签章集成', '移动端审批', '审批流可追溯', '自动归档']
    }
  ];

  const deliverySteps = [
    {
      title: '诊断评估',
      description: '顾问与业务团队联合调研现状，输出痛点清单与数字化路线图'
    },
    {
      title: '方案设计',
      description: '梳理管理流程与数据模型，制定模块上线节奏与成功指标'
    },
    {
      title: '实施落地',
      description: '项目经理驻场推进，提供数据迁移、集成、培训等服务'
    },
    {
      title: '持续运营',
      description: '上线后配置优化、报表扩展与高层驾驶舱运营，确保价值持续兑现'
    }
  ];

  return (
    <div className="product">
      <motion.section
        className="hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <div className="hero-content">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <p className="eyebrow">PRODUCT & SERVICES</p>
              <h1>星晟工程管控系统</h1>
              <p className="intro">
                打通项目、合同、流程与资金全链条，构建工程企业可视、可控、可预测的数字化底座，让管理层实时掌握项目健康度与资金安全。
              </p>
              <div className="hero-actions">
                <button className="primary-button"><a href='/contact'>预约产品演示</a></button>
                <button className="secondary-button">下载产品白皮书</button>
              </div>
            </motion.div>
            <motion.div
              className="hero-panel"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h3 style={{ color: '#FFF' }}>企业级落地效果</h3>
              <ul>
                <li>统一项目、合同、成本数据视图</li>
                <li>跨部门流程在线协同与审计追踪</li>
                <li>AI 风控模型提前预警资金与履约风险</li>
              </ul>
            </motion.div>
          </div>
          <motion.div
            className="hero-stats"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {heroStats.map((stat) => (
              <motion.div
                className="stat-card"
                key={stat.label}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <span className="value" style={{ color: '#0052D9' }}>{stat.value}</span>
                <span className="label" style={{ color: '#0052D9' }}>{stat.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      <motion.section
        className="pain-points"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "0px 0px -100px 0px" }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <div className="section-header">
            <motion.p
              className="eyebrow"
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              PAIN POINTS
            </motion.p>
            <motion.h2
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              工程企业数字化升级的堵点在哪里？
            </motion.h2>
          </div>
          <div className="pain-grid">
            {painPoints.map((point, index) => (
              <motion.div
                className="pain-card"
                key={point.title}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
              >
                <h3>{point.title}</h3>
                <p>{point.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      <motion.section
        className="modules"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "0px 0px -100px 0px" }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <div className="section-header">
            <motion.p
              className="eyebrow"
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              CORE MODULES
            </motion.p>
            <motion.h2
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              四大模块，搭建可复制的项目运营体系
            </motion.h2>
            <motion.p
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              从现场执行到资金穿透，模块之间共享数据模型，帮助管理层快速洞察项目风险并做出决策。
            </motion.p>
          </div>
          <div className="module-grid">
            {modules.map((module, index) => (
              <motion.div
                className="module-card"
                key={module.title}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
              >
                <div className="module-header">
                  <h3>{module.title}</h3>
                  <p>{module.description}</p>
                </div>
                <div className="module-items">
                  {module.items.map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      <motion.section
        className="delivery"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "0px 0px -100px 0px" }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <div className="section-header">
            <motion.p
              className="eyebrow"
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              SERVICE PLAYBOOK
            </motion.p>
            <motion.h2
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              端到端交付方法论，确保价值落地
            </motion.h2>
          </div>
          <div className="delivery-steps">
            {deliverySteps.map((step, index) => (
              <motion.div
                className="step-card"
                key={step.title}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
              >
                <span className="step-index">{index + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      <motion.section
        className="final-cta"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "0px 0px -100px 0px" }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <motion.p
            className="eyebrow"
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            下一步行动
          </motion.p>
          <motion.h2
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            开启工程管理数字化转型，提升企业竞争力
          </motion.h2>
          <motion.p
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            已有数百家工程企业选择星晟，实现项目效率提升 40% 以上，成本控制精度提升 50%，合同履约风险降低 30%。立即预约顾问演示，获取行业评估报告与落地路线图。
          </motion.p>
          <motion.div
            className="cta-actions"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <button className="primary-button">预约产品演示</button>
            <button className="secondary-button">咨询行业专家</button>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default Product;